import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ecosystemApi } from "@/lib/api";
import { PageShell } from "./Overview";
import { Check, Zap, Plug, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";

const Ecosystem = () => {
  const [rows, setRows] = useState([]);
  const [events, setEvents] = useState([]);
  const [busy, setBusy] = useState("");

  const load = () => Promise.all([ecosystemApi.status(), ecosystemApi.events()])
    .then(([s, e]) => { setRows(s); setEvents(e); })
    .catch(() => {});

  useEffect(() => { load(); }, []);

  const act = async (entity_id, product, isActive) => {
    const key = `${entity_id}-${product.id}`;
    setBusy(key);
    try {
      if (isActive) await ecosystemApi.deactivate(entity_id, product.id);
      else await ecosystemApi.activate(entity_id, product.id);
      toast.success(`${product.name} ${isActive ? "deactivated" : "activated"}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Action failed");
    } finally {
      setBusy("");
    }
  };

  if (rows.length === 0) {
    return (
      <PageShell title="Ecosystem" subtitle="Mahakali Tribunal stack">
        <div className="border border-dashed border-border-subtle p-16 text-center">
          <Plug size={36} className="mx-auto text-ink-muted mb-4" strokeWidth={1.25} />
          <div className="h-display text-2xl italic text-ink-muted">No entities yet.</div>
          <p className="mt-2 text-sm text-ink-muted">
            Incorporate your first company to unlock the Sri, Shrika, Marianne and Clarke stack.
          </p>
          <Link to="/app/register" data-testid="ecosystem-register-cta"
            className="mt-6 inline-flex items-center gap-2 bg-artha-amber text-artha-ink px-5 py-3 text-sm hover:bg-artha-cream transition-colors">
            Start incorporation <ArrowUpRight size={14} />
          </Link>
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell title="Ecosystem" subtitle="Mahakali Tribunal · integrated products">
      <p className="text-sm text-ink-muted max-w-2xl mb-10 leading-relaxed">
        Every Artha entity can be wired to the rest of the Mahakali Tribunal stack — capital, financial ops, legal execution, security and identity. Toggle products per entity below.
      </p>

      <div className="space-y-10">
        {rows.map((row) => (
          <section key={row.entity_id} data-testid={`eco-entity-${row.entity_id}`}>
            <div className="flex items-end justify-between mb-4">
              <div>
                <div className="text-xs font-mono-accent uppercase tracking-[0.2em] text-ink-muted">
                  {row.country_name} · {row.entity_status}
                </div>
                <h2 className="h-display text-3xl mt-1">{row.company_name}</h2>
              </div>
              <Link to={`/app/entities/${row.entity_id}`}
                className="text-xs font-mono-accent uppercase tracking-[0.2em] text-artha-amber hover:text-artha-cream">
                Open entity →
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-border-subtle border border-border-subtle">
              {row.products.map((p) => {
                const isActive = p.status === "active";
                const key = `${row.entity_id}-${p.id}`;
                return (
                  <div key={p.id} data-testid={`eco-card-${row.entity_id}-${p.id}`}
                    className="bg-surface-card p-6 flex flex-col">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-3">
                          <span className="inline-block w-2 h-2 rounded-full" style={{ background: p.color }} />
                          <div className="h-display text-2xl">{p.name}</div>
                        </div>
                        <div className="text-[10px] font-mono-accent uppercase tracking-[0.22em] text-ink-muted mt-1">
                          {p.tagline}
                        </div>
                      </div>
                      <Badge active={isActive} />
                    </div>
                    <p className="text-sm text-ink-muted mt-4 leading-relaxed flex-1">{p.description}</p>
                    {isActive && (
                      <div className="mt-5 pt-4 border-t border-border-subtle grid grid-cols-2 gap-3">
                        <Mini v={p.metric_value} l={p.metric_label} />
                        <Mini v={p.reference_id} l="Reference" mono small />
                      </div>
                    )}
                    <button
                      onClick={() => act(row.entity_id, p, isActive)}
                      disabled={busy === key}
                      data-testid={`eco-toggle-${row.entity_id}-${p.id}`}
                      className={`mt-5 inline-flex items-center justify-center gap-2 py-2.5 text-sm transition-colors disabled:opacity-60 ${
                        isActive
                          ? "border border-border-subtle text-ink-muted hover:border-[#C94F4F] hover:text-[#C94F4F]"
                          : "bg-artha-amber text-artha-ink hover:bg-artha-cream"
                      }`}>
                      {isActive ? <><Check size={14} /> {p.cta_unlist}</> : <><Zap size={14} /> {p.cta_list}</>}
                    </button>
                  </div>
                );
              })}
            </div>
          </section>
        ))}
      </div>

      {events.length > 0 && (
        <section className="mt-14">
          <h2 className="h-display text-3xl mb-4">Activity</h2>
          <div className="border border-border-subtle divide-y divide-border-subtle">
            {events.slice(0, 20).map((ev) => (
              <div key={ev.id} className="grid grid-cols-12 gap-4 items-center px-5 py-3" data-testid={`eco-event-${ev.id}`}>
                <div className="col-span-1 font-mono-accent text-[10px] uppercase tracking-[0.22em] text-artha-amber">
                  {ev.event}
                </div>
                <div className="col-span-8 text-sm">{ev.detail}</div>
                <div className="col-span-3 text-right text-xs font-mono-accent text-ink-muted">
                  {new Date(ev.created_at).toLocaleString()}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
    </PageShell>
  );
};

const Badge = ({ active }) => (
  <span className={`text-[10px] font-mono-accent uppercase tracking-[0.22em] px-2 py-1 ${
    active ? "bg-artha-amber/20 text-artha-amber" : "bg-border-subtle text-ink-muted"
  }`}>
    {active ? "Active" : "Inactive"}
  </span>
);

const Mini = ({ v, l, mono, small }) => (
  <div>
    <div className={`${mono ? "font-mono-accent" : "h-display"} ${small ? "text-xs truncate" : "text-2xl"} text-ink-body`}>{v}</div>
    <div className="text-[10px] uppercase tracking-[0.22em] font-mono-accent text-ink-muted mt-1">{l}</div>
  </div>
);

export default Ecosystem;
